<template>
    <section class="relative overflow-hidden bg-lifeboost-gray">
        <div
            class="relative z-10 pb-10 mx-auto max-w-screen-2xl monitor-only:max-w-desktop lg:px-12 3xl:px-10 lg:py-12 monitor-only:px-12 xl:px-16 lg:pt-8 sm:pb-8 md:pb-24">
            <div class="grid grid-cols-12 lg:gap-x-12">
                <div class="grid items-center order-1 col-span-12 px-4 lg:col-span-6 lg:order-2 lg:px-0 md:px-10">
                    <div>
                        <h1 class="mt-6 font-bold text-center lg:text-48 text-53 2xl:text-51 leading-52 lg:mt-0 lg:text-left font-ww-medium text-lifeboost-gray-light"
                            v-html="mainHeading">
                        </h1>
                        <h2 class="mt-4 text-center lg:text-left 2xl:text-27 lg:text-25 text-27 leading-32 font-ww-medium text-lifeboost-gray-lighter"
                            v-html="subheading">
                        </h2>
                        <div class="flex flex-col justify-start mt-8 space-y-6 lg:space-y-8 lg:items-start md:mt-8">
                            <div class="text-center lg:px-0 sm:px-4">
                                <button @click="scrollTo(scrollTarget, scrollPosition(mobilePosition))"
                                    class="flex items-center w-full bnt-icon-hero md:w-auto">
                                    <span class="flex items-center" v-html="buttonText"></span>
                                    <svg class="relative ml-4 top-0.5 sm:w-8 w-8" xmlns="http://www.w3.org/2000/svg"
                                        xmlns:xlink="http://www.w3.org/1999/xlink" width="38px" height="31px"
                                        viewBox="0 0 38 31">
                                        <path fill-rule="evenodd" fill="#FFFFFF"
                                            d="M37.423,17.013 L24.160,30.420 C23.389,31.199 22.139,31.200 21.369,30.422 C20.599,29.643 20.600,28.381 21.371,27.601 L31.183,17.683 L1.631,17.683 C0.730,17.683 -0.001,16.945 -0.001,16.033 L-0.001,15.338 C-0.001,14.426 0.730,13.688 1.631,13.688 L31.541,13.688 L21.362,3.398 C20.592,2.619 20.593,1.357 21.364,0.577 C22.135,-0.202 23.384,-0.203 24.154,0.575 L37.401,13.965 C37.743,14.311 37.924,14.754 37.962,15.206 C38.088,15.839 37.909,16.522 37.423,17.013 Z" />
                                    </svg>
                                </button>
                            </div>

                            <div class="items-center justify-center block lg:mt-0">
                                <div
                                    class="flex flex-col items-center justify-center md:flex-row lg:justify-start lg:block md:gap-y-0 gap-y-3">
                                    <div class="float-left p-1 pr-4">
                                        <nuxt-img :src="ratingImageSrc" alt="stars" class="mx-auto sm:w-32 w-36 lg:w-40"
                                            width="165" height="24" preload format="webp" />
                                    </div>
                                    <div class="float-left mx-auto text-2xl font-bold text-center md:contents lg:mt-0 sm:text-22 font-ww-regular text-lifeboost-gray-light100"
                                        v-html="statsText">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex items-center col-span-12 lg:col-span-6 lg:order-2">
                    <video autoplay muted playsinline loop preload="auto" width="1020" height="661"
                        :poster="videoPosterSrc" class="object-cover shadow-lg lg:rounded-3xl">
                        <source :src="videoSrc" type="video/mp4">
                        Your browser does not support the video tag.
                    </video>
                </div>
            </div>
        </div>

        <div class="relative w-full">
            <nuxt-img v-if="desktopImageSrc" :src="desktopImageSrc"
                class="absolute hidden object-cover w-full -bottom-4 md:block" width="1911" height="835" preload
                loading="eager" fetchpriority="high" format="webp"
                sizes="xs:330px sm:440px md:768px lg:1024px xl:1200px" alt="hero background" />
        </div>
        <nuxt-img v-if="mobileImageSrc" :src="mobileImageSrc"
            class="absolute object-cover w-full -bottom-4 sm:bottom-16 md:hidden" width="620" height="870" preload
            loading="eager" fetchpriority="high" format="webp" sizes="xs:330px sm:440px md:600px lg:720px xl:584px"
            alt="hero mobile background" />
    </section>
</template>

<script setup>
const props = defineProps({
    mainHeading: {
        type: String,
        default: 'All Day Focus & Energy...While Eliminating Brain Fog, Jitters, and Crashes!'
    },
    subheading: {
        type: String,
        default: 'Discover the Mind-Boosting Power of Ultra Clean Coffee Upgraded with Two High-Potency Organic Mushroom Extracts'
    },
    buttonText: {
        type: String,
        default: 'Try it now!'
    },
    scrollTarget: {
        type: String,
        default: 'the-products'
    },
    ratingImageSrc: {
        type: String,
        default: '/assets/images/Group-36.png'
    },
    statsText: {
        type: String,
        default: 'Over 60,000,000 Cups Served of Lifeboost Coffee'
    },
    videoSrc: {
        type: String,
        default: 'https://cdn.shopify.com/videos/c/o/v/b52e1b2a5b8b4ef1a61db7ad3c949290.mp4'
    },
    videoPosterSrc: {
        type: String,
        default: '/assets/images/header-mushroom-small.webp'
    },
    desktopImageSrc: {
        type: String,
        default: '/assets/images/image-top-mushroom.png'
    },
    mobileImageSrc: {
        type: String,
        default: '/assets/images/graphic444.png'
    },
    mobilePosition: {
        type: String,
        default: 'bottom'
    }
});

defineBuilderComponent({
    friendlyName: "Hero-4",
    inputs: [
        {
            name: 'mainHeading',
            type: 'richText',
            friendlyName: 'Main Heading',
            defaultValue: 'All Day Focus & Energy...While Eliminating Brain Fog, Jitters, and Crashes!'
        },
        {
            name: 'subheading',
            type: 'richText',
            friendlyName: 'Subheading',
            defaultValue: 'Discover the Mind-Boosting Power of Ultra Clean Coffee Upgraded with Two High-Potency Organic Mushroom Extracts'
        },
        {
            name: 'buttonText',
            type: 'richText',
            friendlyName: 'Button Text',
            defaultValue: 'Try it now!'
        },
        {
            name: 'scrollTarget',
            type: 'string',
            friendlyName: 'Scroll Target ID',
            defaultValue: 'the-products'
        },
        {
            name: 'mobilePosition',
            type: 'enum',
            friendlyName: 'Scroll Position mobile',
            enum: [
                { label: 'Bottom', value: 'bottom' },
                { label: 'Top', value: 'top' }
            ],
            defaultValue: 'Bottom'
        },
        {
            name: 'ratingImageSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            friendlyName: 'Rating Image Source',
            defaultValue: '/assets/images/Group-36.png'
        },
        {
            name: 'statsText',
            type: 'richText',
            friendlyName: 'Statistics Text',
            defaultValue: 'Over 60,000,000 Cups Served of Lifeboost Coffee'
        },
        {
            name: 'videoSrc',
            type: 'file',
            allowedFileTypes: ['mp4'],
            friendlyName: 'Video Source',
            defaultValue: 'https://cdn.shopify.com/videos/c/o/v/b52e1b2a5b8b4ef1a61db7ad3c949290.mp4'
        },
        {
            name: 'videoPosterSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            friendlyName: 'Video Poster Image',
            defaultValue: '/assets/images/header-mushroom-small.webp'
        },
        {
            name: 'desktopImageSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            friendlyName: 'Desktop Background Image',
            defaultValue: '/assets/images/image-top-mushroom.png'
        },
        {
            name: 'mobileImageSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            friendlyName: 'Mobile Background Image',
            defaultValue: '/assets/images/graphic444.png'
        }
    ]
});
</script>
